import { Link } from "react-router-dom";
import { Shell } from "../components/Shell";

/**
 * Guía paso a paso para onboardear el canal WhatsApp Cloud API
 * en una alcaldía. Pensada para que el equipo TIC del municipio
 * la siga sin necesidad de soporte de MiPueblo.
 *
 * Si actualizas pasos, ajusta también el "duracion" del index.
 */
export function DocsWhatsappPage() {
  return (
    <Shell>
      <section className="page" style={{ maxWidth: 880 }}>
        <div className="eyebrow">
          <Link to="/docs">← Documentación</Link> · WhatsApp Cloud API
        </div>

        <h1 style={{ margin: "8px 0 12px", fontSize: 32 }}>
          WhatsApp Cloud API para alcaldías
        </h1>

        <div className="callout">
          <strong>Tiempo total:</strong> 1-2 horas activas + 1 a 5 días hábiles
          esperando verificaciones de Meta.
        </div>

        {/* ---- Checklist ---- */}
        <Section title="Antes de empezar — checklist">
          <Checklist
            items={[
              <>
                <strong>Email institucional</strong> del responsable (NO
                gmail/hotmail). Ej: <code>tic@cajica.gov.co</code>
              </>,
              <>
                <strong>RUT</strong> vigente de la alcaldía
              </>,
              <>
                <strong>Cámara de Comercio</strong> o{" "}
                <strong>acto administrativo</strong> que acredite representación
                legal
              </>,
              <>
                <strong>Número celular dedicado</strong> SOLO para WhatsApp
                Business (no puede tener cuenta personal de WhatsApp activa).
                Recomiendo SIM nueva.
              </>,
              <>
                <strong>Tarjeta crédito o débito internacional</strong> a nombre
                de la alcaldía (Meta requiere medio de pago aunque las primeras
                1000 conv/mes sean gratis)
              </>,
              <>
                <strong>Dominio web verificable</strong> de la alcaldía (ej:{" "}
                <code>cajica.gov.co</code>)
              </>,
            ]}
          />
        </Section>

        {/* ---- Paso 1 ---- */}
        <Section title="Paso 1 — Crear cuenta de Meta Business Suite">
          <Ol>
            <li>
              Ir a →{" "}
              <ExtLink href="https://business.facebook.com">
                business.facebook.com
              </ExtLink>
            </li>
            <li>
              Click <Kbd>Crear cuenta</Kbd>
            </li>
            <li>Llenar:</li>
            <ul>
              <li>
                <strong>Nombre del negocio:</strong>{" "}
                <code>Alcaldía de [Municipio]</code> (exacto, como en el RUT)
              </li>
              <li>
                <strong>Tu nombre:</strong> el del responsable TIC
              </li>
              <li>
                <strong>Email comercial:</strong> el institucional
              </li>
            </ul>
            <li>Confirmar email (te llega un link)</li>
          </Ol>
        </Section>

        {/* ---- Paso 2 ---- */}
        <Section title="Paso 2 — Configurar info de la empresa">
          <p>
            Dentro de Business Suite → <Kbd>Configuración (⚙️)</Kbd> →{" "}
            <Kbd>Información del negocio</Kbd>:
          </p>
          <Table
            rows={[
              ["Nombre legal", "Alcaldía Municipal de [Municipio]"],
              ["Dirección", "Dirección física de la sede principal"],
              ["Sitio web", "https://[municipio].gov.co"],
              ["Teléfono", "Conmutador oficial"],
              ["Industria", "Government Agency"],
              ["Tax ID / NIT", "NIT con dígito verificación (ej. 899999114-1)"],
            ]}
          />
        </Section>

        {/* ---- Paso 3 ---- */}
        <Section title="Paso 3 — Verificar dominio (opcional pero acelera)">
          <p>Esto le da reputación a tu Business Account.</p>
          <Ol>
            <li>
              Business Suite → <Kbd>Seguridad de la marca</Kbd> →{" "}
              <Kbd>Dominios</Kbd>
            </li>
            <li>
              Click <Kbd>Agregar</Kbd> → escribir <code>cajica.gov.co</code>
            </li>
            <li>
              Meta te da 3 opciones — la más rápida es <strong>meta-tag</strong>
              :
              <pre className="code-block">{`<meta name="facebook-domain-verification" content="abc123xyz" />`}</pre>
              Tu equipo TIC pega esto en el <code>&lt;head&gt;</code> de la home
            </li>
            <li>
              Click <Kbd>Verificar</Kbd> → si está bien, queda en verde
            </li>
          </Ol>
        </Section>

        {/* ---- Paso 4 ---- */}
        <Section title="Paso 4 — Solicitar verificación de la empresa">
          <div className="callout warn">
            Este es el paso CRÍTICO y el más largo (1-5 días).
          </div>
          <Ol>
            <li>
              Business Suite → <Kbd>Centro de Seguridad</Kbd> →{" "}
              <Kbd>Verificación comercial</Kbd>
            </li>
            <li>
              Click <Kbd>Iniciar verificación</Kbd>
            </li>
            <li>Subir documentos:</li>
            <ul>
              <li>
                <strong>RUT</strong> actualizado (PDF, escaneo legible)
              </li>
              <li>
                <strong>Cámara de Comercio</strong> o{" "}
                <strong>Acto administrativo de posesión del alcalde</strong> (lo
                que pruebe representación)
              </li>
              <li>
                <strong>Factura de servicios públicos</strong> a nombre de la
                alcaldía con dirección que coincida
              </li>
            </ul>
            <li>
              Meta puede pedirte info adicional por email — responder rápido
            </li>
            <li>Esperar correo de aprobación</li>
          </Ol>
          <div className="callout tip">
            💡 <strong>Tip:</strong> si Meta rechaza por &quot;no podemos
            confirmar tu negocio&quot;, sube además un screenshot de la página
            oficial del gobierno colombiano que liste la alcaldía (ej. consulta
            DIAN del NIT).
          </div>
        </Section>

        {/* ---- Paso 5 ---- */}
        <Section title="Paso 5 — Crear WhatsApp Business Account (WABA)">
          <p>Una vez verificada la empresa:</p>
          <Ol>
            <li>
              Business Suite → <Kbd>Cuentas</Kbd> →{" "}
              <Kbd>Cuentas de WhatsApp</Kbd> → <Kbd>Agregar</Kbd>
            </li>
            <li>
              Click <Kbd>Crear una cuenta de WhatsApp Business</Kbd>
            </li>
            <li>Llenar:</li>
            <ul>
              <li>
                <strong>Nombre de la cuenta:</strong> Alcaldía de Cajicá
              </li>
              <li>
                <strong>Zona horaria:</strong> America/Bogotá (GMT-5)
              </li>
              <li>
                <strong>Moneda:</strong> COP
              </li>
            </ul>
            <li>Aceptar términos comerciales de WhatsApp</li>
          </Ol>
        </Section>

        {/* ---- Paso 6 ---- */}
        <Section title="Paso 6 — Agregar el número de teléfono">
          <Ol>
            <li>
              Dentro del WABA → <Kbd>Configuración del teléfono</Kbd> →{" "}
              <Kbd>Agregar número</Kbd>
            </li>
            <li>Verificar por SMS o llamada (tener el celular a mano)</li>
            <li>
              Definir <strong>Display Name</strong> (el nombre que ven los
              ciudadanos):
              <ul>
                <li>
                  Debe ser: <code>Alcaldía de [Municipio]</code> o variante
                  reconocible
                </li>
                <li>
                  <strong>NO</strong> puede llevar emojis, palabras genéricas
                  como &quot;Servicio&quot;, o promesas
                </li>
                <li>Meta lo revisa en 1-3 días</li>
              </ul>
            </li>
            <li>
              Mientras se aprueba el display name, el número ya funciona pero
              aparece como número crudo
            </li>
          </Ol>
        </Section>

        {/* ---- Paso 7 ---- */}
        <Section title="Paso 7 — Crear System User (token permanente)">
          <p>Esto te da un token permanente (no expira como el de usuario).</p>
          <Ol>
            <li>
              Business Suite → <Kbd>Configuración</Kbd> → <Kbd>Usuarios</Kbd> →{" "}
              <Kbd>Usuarios del sistema</Kbd>
            </li>
            <li>
              Click <Kbd>Agregar</Kbd>:
              <ul>
                <li>
                  <strong>Nombre:</strong> MiPueblo Integration
                </li>
                <li>
                  <strong>Rol:</strong> Admin
                </li>
              </ul>
            </li>
            <li>
              Click en el nuevo system user → <Kbd>Agregar activos</Kbd>:
              <ul>
                <li>
                  <strong>Cuentas de WhatsApp</strong> → seleccionar tu WABA →
                  permisos: <code>Manage</code>
                </li>
              </ul>
            </li>
            <li>
              Click <Kbd>Generar token</Kbd>:
              <ul>
                <li>
                  <strong>App:</strong> la app de Meta (ver Paso 7.1 si no
                  tienes)
                </li>
                <li>
                  <strong>Vencimiento:</strong> <code>Never</code>
                </li>
                <li>
                  <strong>Permisos:</strong>{" "}
                  <code>whatsapp_business_messaging</code>,{" "}
                  <code>whatsapp_business_management</code>
                </li>
              </ul>
            </li>
            <li>
              <strong>GUARDA el token en lugar seguro</strong> — solo se muestra
              una vez.
            </li>
          </Ol>

          <h4 style={{ marginTop: 20 }}>
            Paso 7.1 — Crear App de Meta (si no tienes)
          </h4>
          <Ol>
            <li>
              Ir a →{" "}
              <ExtLink href="https://developers.facebook.com/apps">
                developers.facebook.com/apps
              </ExtLink>
            </li>
            <li>
              <Kbd>Crear app</Kbd> → tipo: <code>Otro</code> →{" "}
              <code>Empresa</code>
            </li>
            <li>
              Nombre: <code>MiPueblo - Cajicá</code> (o similar)
            </li>
            <li>Asociarla a tu Business Account</li>
            <li>
              Agregar producto <code>WhatsApp</code>
            </li>
          </Ol>
        </Section>

        {/* ---- Paso 8 ---- */}
        <Section title="Paso 8 — Configurar en MiPueblo">
          <p>Ya tienes los 3 datos que MiPueblo necesita:</p>
          <Table
            rows={[
              [
                "Phone Number ID",
                "WhatsApp Manager → tu número → cabecera, ej. 123456789012345",
              ],
              [
                "WABA ID",
                "WhatsApp Manager → arriba a la izquierda, ej. 987654321098765",
              ],
              ["Access Token", "El que generaste en Paso 7"],
            ]}
          />
          <p>
            <strong>En el panel super-admin de MiPueblo:</strong>
          </p>
          <Ol>
            <li>
              Ir a <code>panel.mipuebloapp.com/tenants/[slug]/whatsapp</code>
            </li>
            <li>Pegar los tres campos</li>
            <li>
              Click <Kbd>Guardar</Kbd>
            </li>
            <li>
              MiPueblo registra automáticamente el webhook en Meta — debería
              marcar <strong style={{ color: "var(--ok)" }}>Activo ✅</strong>
            </li>
            <li>Mandar un mensaje de prueba al número desde otro celular</li>
          </Ol>
        </Section>

        {/* ---- Errores comunes ---- */}
        <Section title="Errores comunes">
          <Table
            head={["Síntoma", "Causa", "Solución"]}
            rows={[
              [
                "Display name rejected",
                "Nombre genérico o con emojis",
                "Cambiar por Alcaldía de [Municipio] exacto",
              ],
              [
                "Business verification rejected",
                "Documentos ilegibles o nombre no coincide con RUT",
                "Re-subir en PDF nítido, asegurar nombre exacto",
              ],
              [
                "Mensajes salen pero no entran",
                "Webhook no configurado",
                "Verificar en panel que el toggle esté en Activo",
              ],
              [
                "Phone number already registered",
                "El celular tenía WhatsApp personal o WA Business app",
                "Desinstalar todo, esperar 30 min, reintentar",
              ],
            ]}
          />
        </Section>

        {/* ---- Modo prueba ---- */}
        <Section title="Modo prueba — sin verificar empresa (10 min)">
          <p>
            Si solo quieres testear el flujo, Meta te da un{" "}
            <strong>número de prueba gratis</strong> con 5 destinatarios
            permitidos, sin necesidad de hacer toda la verificación arriba.
          </p>
          <Ol>
            <li>
              Crear cuenta de developer en{" "}
              <ExtLink href="https://developers.facebook.com">
                developers.facebook.com
              </ExtLink>{" "}
              (sirve tu Facebook personal).
            </li>
            <li>
              <Kbd>Crear app</Kbd> → caso de uso <code>Otro</code> → tipo{" "}
              <code>Empresa</code>. Nombre: <code>MiPueblo Test</code>. NO
              asocies Business account.
            </li>
            <li>
              Agregar producto <strong>WhatsApp</strong> → Meta crea
              automáticamente: WABA de prueba, número de prueba, Phone Number
              ID, token temporal de 24h.
            </li>
            <li>
              Agregar destinatarios permitidos (máx 5): pantalla WhatsApp →{" "}
              <Kbd>Manage phone number list</Kbd> → tu celular en formato{" "}
              <code>+57 300 1234567</code>.
            </li>
            <li>
              Probar desde la consola de Meta antes de tocar MiPueblo →{" "}
              <Kbd>Send message</Kbd>.
            </li>
            <li>Pegar los 3 datos en el panel super-admin del tenant.</li>
          </Ol>

          <p style={{ marginTop: 14, fontWeight: 600 }}>
            Limitaciones del modo prueba:
          </p>
          <Table
            rows={[
              ["Destinatarios", "Máx 5 números registrados"],
              ["Mensajes", "1.000/mes gratis"],
              ["Display name", "Aparece como número crudo"],
              [
                "Token",
                "Expira en 24 horas — hay que regenerarlo en la misma pantalla",
              ],
              ["Webhook", "Funciona normal"],
            ]}
          />
        </Section>
      </section>
    </Shell>
  );
}

/* ============ helpers de presentación ============ */

function Section({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section
      style={{
        marginTop: 32,
        paddingTop: 24,
        borderTop: "1px solid var(--hairline)",
      }}
    >
      <h2 style={{ fontSize: 20, marginTop: 0, marginBottom: 14 }}>{title}</h2>
      <div style={{ lineHeight: 1.6, color: "var(--ink-2)", fontSize: 14.5 }}>
        {children}
      </div>
    </section>
  );
}

function Ol({ children }: { children: React.ReactNode }) {
  return (
    <ol style={{ paddingLeft: 22, margin: "8px 0", display: "grid", gap: 6 }}>
      {children}
    </ol>
  );
}

function Checklist({ items }: { items: React.ReactNode[] }) {
  return (
    <ul style={{ listStyle: "none", padding: 0, margin: "8px 0", display: "grid", gap: 8 }}>
      {items.map((it, i) => (
        <li key={i} style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
          <span
            aria-hidden
            style={{
              flexShrink: 0,
              width: 16,
              height: 16,
              border: "1.5px solid var(--ink-3)",
              borderRadius: 4,
              marginTop: 3,
            }}
          />
          <span>{it}</span>
        </li>
      ))}
    </ul>
  );
}

function Kbd({ children }: { children: React.ReactNode }) {
  return (
    <span
      style={{
        background: "var(--bone-warm)",
        border: "1px solid var(--hairline-strong)",
        borderRadius: 4,
        padding: "1px 6px",
        fontFamily: "var(--font-mono)",
        fontSize: 12.5,
        whiteSpace: "nowrap",
      }}
    >
      {children}
    </span>
  );
}

function ExtLink({ href, children }: { href: string; children: React.ReactNode }) {
  return (
    <a href={href} target="_blank" rel="noreferrer">
      {children} ↗
    </a>
  );
}

function Table({
  head,
  rows,
}: {
  head?: string[];
  rows: (string | React.ReactNode)[][];
}) {
  return (
    <div
      style={{
        marginTop: 10,
        border: "1px solid var(--hairline)",
        borderRadius: 8,
        overflow: "hidden",
      }}
    >
      <table
        style={{
          width: "100%",
          borderCollapse: "collapse",
          fontSize: 13.5,
        }}
      >
        {head && (
          <thead>
            <tr style={{ background: "var(--bone-warm)" }}>
              {head.map((h, i) => (
                <th
                  key={i}
                  style={{
                    textAlign: "left",
                    padding: "8px 12px",
                    fontFamily: "var(--font-mono)",
                    fontSize: 10.5,
                    letterSpacing: "0.08em",
                    textTransform: "uppercase",
                    color: "var(--ink-3)",
                    fontWeight: 500,
                    borderBottom: "1px solid var(--hairline)",
                  }}
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
        )}
        <tbody>
          {rows.map((row, i) => (
            <tr
              key={i}
              style={{
                borderTop: i === 0 && !head ? 0 : "1px solid var(--hairline)",
              }}
            >
              {row.map((cell, j) => (
                <td
                  key={j}
                  style={{
                    padding: "10px 12px",
                    verticalAlign: "top",
                    fontWeight: j === 0 ? 600 : 400,
                  }}
                >
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
